import React, {useState} from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderIcon from "@mui/icons-material/Folder";
import {Accordion, AccordionDetails, AccordionSummary, Grid, Typography} from "@mui/material";
import _ from "lodash";
import {useSelector} from "react-redux";
import {getPackageInternalUrl} from "../../constants/appRoutes";
import useLanguages from "../../hooks/useLanguages";
import {CategoryGroupModelView} from "../../model/item-containers-models/categoryGroupModelView";
import {CategoryModelView} from "../../model/item-containers-models/categoryModelView";
import {DatasetModelView} from "../../model/item-containers-models/datasetModelView";
import {ItemCategoryTemplateDto} from "../../model/item-containers-models/itemCategoryTemplateDto";
import PackageCard from "../package-card";
import CatalogPortionPreview from "./CatalogPortionPreview";
import {hubNodesSelector} from "../../state/hub/hubSelectors";
import {themeConfigSelector} from "../../state/theme-config/themeConfigSelectors";
import {getCatalog} from "../../utils/catalog";
import {findSelectedCategory, getAgencyAndCategoryFromFullId} from "../../utils/itemContainers";

const isCategoryGroup = (v: CategoryModelView | CategoryGroupModelView): v is CategoryGroupModelView =>
  v.hasOwnProperty("categories");
const getChildrenCategories = (v: CategoryModelView | CategoryGroupModelView) =>
  isCategoryGroup(v) ? v.categories : v.childrenCategories;
const getPackageIdentifiers = (v: CategoryModelView | CategoryGroupModelView) =>
  !isCategoryGroup(v) ? v.packageIdentifiers : [];

const ItemContainerCategoriesList = ({item: _item}: {item: ItemCategoryTemplateDto}) => {
  const {t} = useLanguages();
  const themeConfig = useSelector(themeConfigSelector);

  const item = _.cloneDeep(_item);
  item.catalog = getCatalog(item.catalog, false, t);

  const {agencyId: selectedAgencyId, categoryId: selectedCategoryId} = getAgencyAndCategoryFromFullId(item.category);
  const agency = item.catalog.categoryGroups.find(agency => agency.id === selectedAgencyId);
  const category = findSelectedCategory(selectedCategoryId, agency);
  const childrenCategories = getChildrenCategories(category);
  const [selectedCategory, setSelectedCategory] = useState<CategoryModelView>(childrenCategories[0]);

  if (item.enableCatalogPortion) {
    return (
      <>
        {selectedCategory !== undefined ? (
          <>
            <CatalogPortionPreview
              childrenCategories={childrenCategories}
              setInitialCategory={id => setSelectedCategory(childrenCategories.find(el => el.id === id))}
              partialPath={[selectedCategory]}
              columnDimensionsConfig={themeConfig.CatalogPortionPreviewProps}
            />
            <Category
              item={item}
              path={[category.id, selectedCategory.id]}
              packageMap={item.catalog.packageMap}
              category={selectedCategory}
              level={0}
            />
          </>
        ) : (
          <Typography variant="h5">{t("components.itemContainer.categoryPreview.noChildren")}</Typography>
        )}
      </>
    );
  } else {
    return (
      <>
        <Category item={item} path={[category.id]} packageMap={item.catalog.packageMap} category={category} level={0} />
      </>
    );
  }
};

const Category = ({
  path,
  category,
  packageMap,
  level,
  item
}: {
  path: string[];
  category: CategoryModelView | CategoryGroupModelView;
  packageMap: {[key: string]: DatasetModelView};
  level: number;
  item: ItemCategoryTemplateDto;
}) => {
  return (
    <>
      {level !== 0 && (
        <Accordion
          style={{marginBottom: "10px", marginLeft: `${40 * (level - 1)}px`}}
          defaultExpanded={item.expandCategories}
          disableGutters
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              className="itemContainer__category-item__category-label"
              component="span"
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "20px"
              }}
            >
              <FolderIcon sx={{marginRight: "8px"}} />
              {category.label}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PackageList item={item} identifiers={getPackageIdentifiers(category)} packageMap={packageMap} />
          </AccordionDetails>
        </Accordion>
      )}
      {getChildrenCategories(category)?.map(childCategory => (
        <Category
          item={item}
          key={childCategory.id}
          path={path.concat(childCategory.id)}
          category={childCategory}
          packageMap={packageMap}
          level={level + 1}
        />
      ))}
      {level === 0 && <PackageList item={item} identifiers={getPackageIdentifiers(category)} packageMap={packageMap} />}
    </>
  );
};

const PackageList = ({
  identifiers = [],
  packageMap,
  item
}: {
  identifiers: string[];
  packageMap: {[key: string]: DatasetModelView};
  item: ItemCategoryTemplateDto;
}) => {
  const nodes = useSelector(hubNodesSelector);
  const nodeCode = nodes.find(n => n.nodeId === item.nodeId).code;
  return identifiers.length > 0 ? (
    <Grid container spacing={2} className="itemContainer__category-item__dataset-list">
      {identifiers.map(packageId => (
        <PackageCard
          key={packageId}
          packageId={packageId}
          package={packageMap[packageId]}
          catalog={item.catalog}
          xs={12}
          onClick={undefined}
          md={undefined}
          to={getPackageInternalUrl(nodeCode, [], packageId, undefined)}
          nodeId={item.nodeId}
          isMultiCategorized={undefined}
          categoryItem={item}
          enableBrowseData
          enableMetadataAsButton
          enableSDMXDownload
        />
      ))}
    </Grid>
  ) : null;
};

export default ItemContainerCategoriesList;
